import { JobStatus } from "@/apiClient/models/JobStatus";
import type { ChangeStorageTierParams } from "@/apiClient/models/ChangeStorageTierParams";
import type { TieringJobResponse } from "@/apiClient/models/TieringJobResponse";
import AssetPathList, {
  useAssetPathsByPublicId,
} from "@/components/storageOps/AssetPathList";
import Badge from "@/components/storageOps/Badge";
import JobSummary, { CopyableJobId } from "@/components/storageOps/JobSummary";
import {
  JOB_STATUS_BADGE_CLASSES,
  ROW_STATUS_BADGE_CLASSES,
  formatAgeSince,
} from "@/components/storageOps/storageOps.utils";

const ROW_STATUS_ORDER = [
  "requested",
  "applying",
  "rehydrating",
  "applied",
  "failed",
  "cancelled",
];

type FeeGateDetail = {
  early_deletion_violations?: number;
  sample_asset_public_ids?: string[];
};

const feeGateDetail = (job: TieringJobResponse): FeeGateDetail | null =>
  (job.status_detail as FeeGateDetail | null | undefined) ?? null;

function RowStatusCounts({ counts }: { counts?: Record<string, number> }) {
  const entries = Object.entries(counts ?? {}).filter(([, count]) => count > 0);
  if (entries.length === 0) {
    return <span className="text-xs text-grey-200">No rows marked yet</span>;
  }

  entries.sort(
    ([a], [b]) => ROW_STATUS_ORDER.indexOf(a) - ROW_STATUS_ORDER.indexOf(b)
  );

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([status, count]) => (
        <Badge
          key={status}
          label={`${status}: ${count.toLocaleString()}`}
          colorClasses={ROW_STATUS_BADGE_CLASSES[status]}
        />
      ))}
    </div>
  );
}

function FeeGateFailureDetail({
  detail,
  pathByPublicId,
}: {
  detail: FeeGateDetail | null;
  pathByPublicId: Map<string, string | undefined>;
}) {
  if (!detail?.early_deletion_violations) {
    return null;
  }

  return (
    <div className="mt-2 rounded-md border border-amber-100 bg-amber-50 p-2 text-xs text-amber-700">
      <p className="font-semibold">
        Blocked by fee gate: {detail.early_deletion_violations.toLocaleString()}{" "}
        rows would incur early-deletion fees.
      </p>
      <AssetPathList
        publicIds={detail.sample_asset_public_ids ?? []}
        pathByPublicId={pathByPublicId}
      />
    </div>
  );
}

const jobHasFeeGateFailure = (job: TieringJobResponse): boolean =>
  job.status === JobStatus.FAILURE &&
  Boolean(feeGateDetail(job)?.early_deletion_violations);

function TieringJobsPanel({
  jobs,
  isLoading,
  onCancelJob,
  onRerunWithFeesAllowed,
  onRetierCancelledRows,
  pendingJobAction,
}: {
  jobs: TieringJobResponse[];
  isLoading: boolean;
  onCancelJob: (jobPublicId: string) => void;
  onRerunWithFeesAllowed: (job: TieringJobResponse) => void;
  onRetierCancelledRows: (jobPublicId: string) => void;
  pendingJobAction: string | null;
}) {
  // One batched path lookup for every fee-gate sample across the whole list.
  const pathByPublicId = useAssetPathsByPublicId(
    jobs.flatMap((job) => feeGateDetail(job)?.sample_asset_public_ids ?? [])
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-3">
        <h2 className="text-lg font-semibold text-blue-200">
          Job History & Progress
        </h2>
      </div>

      {isLoading ? (
        <p className="px-5 py-4 text-sm text-grey-200">Loading...</p>
      ) : jobs.length === 0 ? (
        <p className="px-5 py-4 text-sm text-grey-200">No tiering jobs yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {jobs.map((job) => {
            const jobPublicId = job.public_id ?? "";
            const submittedAt = job.created_dt ?? null;
            const isRunning =
              job.status === JobStatus.RUNNING ||
              job.status === JobStatus.QUEUED ||
              job.status === JobStatus.CREATED;
            const hasPendingRows =
              (job.row_status_counts?.requested ?? 0) > 0 ||
              (job.row_status_counts?.applying ?? 0) > 0;
            const hasCancelledRows =
              (job.row_status_counts?.cancelled ?? 0) > 0;
            const isActionPending = pendingJobAction === jobPublicId;

            return (
              <li key={jobPublicId} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-blue-200">
                      <JobSummary
                        params={
                          job.params as Partial<ChangeStorageTierParams> | null
                        }
                      />
                      <Badge
                        label={job.status}
                        colorClasses={JOB_STATUS_BADGE_CLASSES[job.status]}
                      />
                      {submittedAt && (
                        <span className="text-xs font-normal text-grey-200">
                          submitted {formatAgeSince(submittedAt)} ago
                        </span>
                      )}
                      <CopyableJobId publicId={jobPublicId} />
                    </p>
                    <div className="mt-1.5">
                      <RowStatusCounts counts={job.row_status_counts} />
                    </div>
                    <FeeGateFailureDetail
                      detail={feeGateDetail(job)}
                      pathByPublicId={pathByPublicId}
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {(isRunning || hasPendingRows) && (
                      <button
                        type="button"
                        className="ox-red-button !mt-0 w-auto !rounded-[8px] !px-3 !py-1 !text-xs"
                        disabled={isActionPending}
                        onClick={() => onCancelJob(jobPublicId)}
                      >
                        {isRunning ? "Cancel" : "Cancel pending"}
                      </button>
                    )}
                    {jobHasFeeGateFailure(job) && (
                      <button
                        type="button"
                        className="ox-button !mt-0 w-auto !rounded-[8px] !px-3 !py-1 !text-xs"
                        disabled={isActionPending}
                        onClick={() => onRerunWithFeesAllowed(job)}
                      >
                        Re-run with fees allowed
                      </button>
                    )}
                    {hasCancelledRows && (
                      <button
                        type="button"
                        className="ox-button !mt-0 w-auto !rounded-[8px] !px-3 !py-1 !text-xs"
                        disabled={isActionPending}
                        onClick={() => onRetierCancelledRows(jobPublicId)}
                      >
                        Re-tier cancelled rows
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default TieringJobsPanel;
